// @ts-strict-ignore
import React, { createContext, useContext, useEffect, useReducer, useRef, useState } from "react";
import { connect } from "react-redux";
import LRUCache from "lru-cache";

import reducer from "./reducer";
import {
    CustomizationModalActions,
    ICustomizationImagesPayload,
    ICustomizationModalState,
    TCustomizationContextType,
} from "./actions";
import { IStore } from "reducers";
import {
    cancelFetchCustomizationImages,
    checkCustomizationImages,
    fetchCompany,
    checkImagesTab,
    fetchExistingCustomizationImages,
    resetExistingImagesList,
    setImagesToExistingImagesList,
} from "actions/customization-actions";

import {
    CustomizationImageFilterDictionary,
    CustomizationImageStatus,
    ImagePermissionType,
    IPrintType,
    IPrintTypeId,
    ISettingsValue,
    ProductCustomizationSettingsMap,
} from "constants/product-customizations/customization";

export interface IStateProps {
    existingImagesFromRedux: ICustomizationImagesPayload;
    companyId: string;
}

export interface IProps {}

export interface IDispatchProps {
    onFetchImages: (
        forMerchantId?: string,
        searchValue?: string,
        filters?: CustomizationImageFilterDictionary,
        status?: CustomizationImageStatus,
    ) => void;
    onCheckImages: (
        forMerchantId?: string,
        searchValue?: string,
        filters?: CustomizationImageFilterDictionary,
        ids?: string[],
    ) => void;
    onCancelFetchImages: () => void;
    onResetExistingImagesList: () => void;
    onCheckTab: (forMerchantId?: string, ids?: string[]) => void;
    onSetImagesToExistingImagesList: (storedValues: ICustomizationImagesPayload) => void;
    onFetchCompany: (companyId: string) => void;
}

const getImagesInitialState = (): ICustomizationImagesPayload => ({
    images: [],
    nextPageToken: null,
});

const getInitialState = (): ICustomizationModalState => ({
    currentAttributeId: "",
    currentMerchantId: "",
    currentTabCacheKey: "",
    fetchParams: {
        merchantId: "",
        searchString: "",
    },
    isFetching: false,
    isChecking: false,
    existingImages: getImagesInitialState(),
    imageFilters: null,
    printSettings: new Map<IPrintTypeId, IPrintType>(),
});

const CustomizationModalContext = createContext<TCustomizationContextType | null>(null);

export const CustomizationModalContextProvider: React.FC<IProps & IStateProps & IDispatchProps> = ({
    children,
    onFetchImages,
    onCheckImages,
    onSetImagesToExistingImagesList,
    existingImagesFromRedux,
    onCancelFetchImages,
    onResetExistingImagesList,
    onFetchCompany,
    companyId,
    onCheckTab,
}) => {
    const [state, dispatch] = useReducer(reducer, getInitialState());
    const [imageCache] = useState(
        new LRUCache({
            // Arbitrary number. 500 entries seem good enough.
            max: 500,
            // 1 hour
            ttl: 1000 * 60 * 60,
        }),
    );
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        return () => onResetExistingImagesList();
    }, [onResetExistingImagesList]);

    useEffect(() => {
        dispatch({ type: CustomizationModalActions.ImagesFetchEnd });

        const cacheKey = getExistingImagesCacheKey();
        imageCache.set(cacheKey, existingImagesFromRedux);

        dispatch({ type: CustomizationModalActions.SetExistingImages, payload: existingImagesFromRedux });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingImagesFromRedux]);

    useEffect(() => {
        if (companyId) {
            onFetchCompany(companyId);
        }
    }, [companyId, onFetchCompany]);

    const handleAttributeIdChange = (id: string): void => {
        dispatch({ type: CustomizationModalActions.SetCurrentAttributeId, payload: id });
    };

    const handleTabChange = (tabType?: string): void => {
        const currentState = stateRef.current;

        if (currentState.isFetching) {
            onCancelFetchImages();
        }

        dispatch({ type: CustomizationModalActions.SetMerchantId, payload: tabType });
        dispatch({ type: CustomizationModalActions.SetExistingImages, payload: getImagesInitialState() });
    };

    const getSearchString = (): string => {
        const cacheKey = getSearchStringCacheKey();

        return imageCache.get(cacheKey) || "";
    };

    const saveSearchString = (searchString: string | null): void => {
        const cacheKey = getSearchStringCacheKey();

        imageCache.set(cacheKey, searchString);
    };

    const fetchImages = (
        merchantId: string | undefined,
        searchString: string,
        filters?: CustomizationImageFilterDictionary,
        status?: CustomizationImageStatus,
    ): void => {
        dispatch({ type: CustomizationModalActions.ImagesFetchStart, payload: { merchantId, searchString } });

        onFetchImages(merchantId, searchString, filters, status);
    };

    const checkImages = (
        merchantId: string | undefined,
        searchString: string,
        filters?: CustomizationImageFilterDictionary,
        ids?: string[],
    ): void => {
        dispatch({ type: CustomizationModalActions.ImagesFetchStart, payload: { merchantId, searchString } });
        if (merchantId === ImagePermissionType[ImagePermissionType.BRAND]) {
            merchantId = undefined;
        }

        onCheckImages(merchantId, searchString, filters, ids);
    };

    const checkTab = (merchantId?: string, ids?: string[]): void => onCheckTab(merchantId, ids);

    const resetImagesList = (): void => {
        const cacheKey = getExistingImagesCacheKey();

        if (!imageCache.has(cacheKey)) {
            // Add null value to the empty image array to force fetch images from server
            const initialImagesState: ICustomizationImagesPayload = {
                images: [null],
                nextPageToken: null,
            };
            onSetImagesToExistingImagesList(initialImagesState);
            return;
        }

        const storedImages = imageCache.get(cacheKey);
        onSetImagesToExistingImagesList(storedImages);
    };

    const handleImageFiltersChange = (imageFilters?: CustomizationImageFilterDictionary): void => {
        dispatch({ type: CustomizationModalActions.SetImageFilters, payload: imageFilters });
    };

    const getSearchStringCacheKey = (): string => {
        const currentState = stateRef.current;

        return `${currentState.currentTabCacheKey}_searchString`;
    };

    const getExistingImagesCacheKey = () => {
        const currentState = stateRef.current;
        const searchString = getSearchString();
        const searchStringKey = searchString ? `_${searchString}` : "";

        return `${currentState.currentTabCacheKey}${searchStringKey}_existingImages`;
    };

    const setPrintSettings = (settings: ISettingsValue[]): void => {
        const printSettingsMap = settings.reduce(
            (dictionary: ProductCustomizationSettingsMap, settingsValue: ISettingsValue) => {
                dictionary.set(settingsValue._id, settingsValue.name);

                return dictionary;
            },
            new Map<IPrintTypeId, IPrintType>(),
        );

        dispatch({ type: CustomizationModalActions.SetProductCustomizationPrintSettings, payload: printSettingsMap });
    };

    return (
        <CustomizationModalContext.Provider
            value={{
                ...state,
                handleAttributeIdChange,
                handleTabChange,
                getSearchString,
                saveSearchString,
                fetchImages,
                checkImages,
                resetImagesList,
                handleImageFiltersChange,
                setPrintSettings,
                checkTab,
            }}
        >
            {children}
        </CustomizationModalContext.Provider>
    );
};

export const useCustomizationModalContext = (): TCustomizationContextType => {
    return useContext(CustomizationModalContext);
};

const mapStateToProps = (state: IStore): IStateProps => {
    const { existingImages } = state.customization;

    return {
        existingImagesFromRedux: existingImages?.payload,
        companyId: state.scope?.company?._id,
    };
};

const mapDispatchToProps = (dispatch): IDispatchProps => ({
    onFetchImages: (
        forMerchantId?: string,
        search?: string,
        filters?: CustomizationImageFilterDictionary,
        status?: CustomizationImageStatus,
    ) => dispatch(fetchExistingCustomizationImages(forMerchantId, search, filters, status)),
    onCheckImages: (
        forMerchantId?: string,
        search?: string,
        filters?: CustomizationImageFilterDictionary,
        ids?: string[],
    ) => dispatch(checkCustomizationImages(forMerchantId, search, filters, ids)),
    onCancelFetchImages: () => dispatch(cancelFetchCustomizationImages()),
    onResetExistingImagesList: () => dispatch(resetExistingImagesList()),
    onCheckTab: (merchantId: string, ids: string[]) => dispatch(checkImagesTab(merchantId, ids)),
    onSetImagesToExistingImagesList: (storedValues: ICustomizationImagesPayload) =>
        dispatch(setImagesToExistingImagesList(storedValues)),
    onFetchCompany: (companyId: string) => dispatch(fetchCompany(companyId)),
});

export const CustomizationModalProvider = connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps,
)(CustomizationModalContextProvider);
